import type { RefObject } from 'react';

import { inMode } from 'js/analytics/config/permissions.wjs.jsx';
import { tabFromLocation } from 'js/analytics/metrics/tabActivityTracking';
import { trackClick } from 'js/utils/trackClick';
import { useElementImpression } from 'js/utils/impressions/useElementImpression';
import {
  ImpressionEntity,
  watchImpressions,
} from 'js/utils/impressions/watchImpressions';
import type { Watcher } from 'js/utils/impressions/watchImpressions';

type AeduWithWorkConstants = typeof window.Aedu & {
  WorkConstants?: {
    CV_WORK_ID: number;
  };
};

export const currentAnalyticsTab = (): string => {
  const tab = tabFromLocation();
  if (typeof window === 'undefined') {
    return tab.replace(/-/g, '_');
  }

  const cvDocument = window.location.pathname.match(
    /\/Analytics\/activity\/documents\/([^/?#]+)/i,
  );
  const cvWorkId = (window.Aedu as AeduWithWorkConstants | undefined)
    ?.WorkConstants?.CV_WORK_ID;

  if (
    cvDocument &&
    cvWorkId !== undefined &&
    cvDocument[1] === String(cvWorkId)
  ) {
    return 'cv';
  }
  return tab.replace(/-/g, '_');
};

const isPremium = (): boolean => Boolean(inMode('premium'));

const analyticsExtra = (
  extra: Record<string, unknown>,
): Record<string, unknown> => ({
  tab: currentAnalyticsTab(),
  is_premium: isPremium(),
  ...extra,
});

export const trackAnalyticsClick = (
  source: string,
  extra: Record<string, unknown> = {},
  entityId?: number | null,
): void => {
  trackClick({
    source,
    entityId,
    extra: analyticsExtra(extra),
  });
};

export const analyticsImpressionId = (key: string): number => {
  let hash = 5381;
  for (let index = 0; index < key.length; index += 1) {
    hash = Math.imul(hash, 33) ^ key.charCodeAt(index);
  }
  return (hash >>> 0) & 0x7fffffff || 1;
};

export const watchAnalyticsImpression = (
  element: HTMLElement,
  source: string,
  key: string,
  extra: Record<string, unknown> = {},
): Watcher =>
  watchImpressions(element, {
    source,
    entity_id: analyticsImpressionId(key),
    entity_type: ImpressionEntity.UnMapped,
    extra: analyticsExtra(extra),
  });

export const useAnalyticsImpression = (
  ref: RefObject<HTMLElement | null>,
  source: string,
  key: string,
  extra: Record<string, unknown> = {},
  active = true,
): void => {
  useElementImpression(ref, {
    source,
    entity_id: analyticsImpressionId(key),
    entity_type: ImpressionEntity.UnMapped,
    extra: analyticsExtra(extra),
    active,
  });
};
